import { useSelector } from "react-redux";
import { allActiveChallenges, allUpcomingChallenges } from "../slices/challengesSlice";
import { filterDocumentsInFS, updateVideoInFS } from "./firebase/firestore";
import { calculateDaysRemaining } from "./momentJS";

export function useScanChallengesForVideos(_videoID) {
   const challengeAnnouncements = useSelector(allUpcomingChallenges);
   const activeChallenges = useSelector(allActiveChallenges);
}

/* 
===== START: Functions used for challenges that end on a certain date. =====
*/

export const findChallengesWithEndDates = activeChallengeArray => activeChallengeArray.filter(challenge => challenge.activeChallengeProperties?.activeChallengeEndDate);

export const findExpiredActiveChallenges = adjustedActiveChallengeEndDate => adjustedActiveChallengeEndDate.filter(challenge => challenge.activeChallengeProperties.activeChallengeEndDate > 1);

/*
===== END: Functions used for challenges that end on a certain date. =====
*/

//===== FIND CHALLENES THAT ENDS ON A CERTAIN DATE. =====
export const findChallengesWithEndDate = () => filterDocumentsInFS(["internet-idol-faceoff", "Challenges", "activeChallenges"], "activeChallengeProperties.howWillActiveChallengeEnd", "end date is reached."); //Promise.
//=======================================================

export const updateDaysRemainingForChallengesWithEndDate = findChallengesWithEndDate => {
   const challenges = findChallengesWithEndDate;
   const challengesWithUpdatedDaysRemaining = challenges.length ? challenges.map(challenge => {
      const { activeChallengeProperties, activeChallengeProperties: { activeChallengeEndDate } } = challenge;

      const daysRemaining = calculateDaysRemaining(activeChallengeEndDate)

      const updatedActiveChallengeProperties = {
         ...activeChallengeProperties,
         activeChallengeDaysRemaining: daysRemaining,
         endActiveChallenge: daysRemaining < 1 ? true : false
      };

      return { ...challenge, activeChallengeProperties: { ...activeChallengeProperties, ...updatedActiveChallengeProperties } };
   }) : []
   
   return new Promise((res, rej) => {
      if (challengesWithUpdatedDaysRemaining.length) res(challengesWithUpdatedDaysRemaining);
      else rej({ message: "Array is empty. There are no challenges that needed their days remaining updated.", challengesWithUpdatedDaysRemaining });
   })
} //Promise. Updates days remaining AND endActiveChallenge status and reutrns all challenges with updated info..

export const returnExpiredChallengesOnly = updateDaysRemainingForChallengesWithEndDate => {
   const challenges = updateDaysRemainingForChallengesWithEndDate;
   
   const expiredChallenges = challenges.filter(challenge => {
      const { activeChallengeProperties, activeChallengeProperties: { activeChallengeDaysRemaining, endActiveChallenge } } = challenge;
      
      return (activeChallengeDaysRemaining < 1) && (endActiveChallenge);
   })

   return new Promise((res, rej) => {
      if (expiredChallenges.length) {
         res(expiredChallenges);
      } else {
         rej({ message: "There are no expired challenges. All active challenge are still current.", expiredChallenges, activeChallenges: challenges });
      }
   })
}

//ActiveChallenge Functions.
export const returnVideoWinners = (challengeVideos) => {
   const highestVote = Math.max(...Object.values(challengeVideos).map(video => video.votes));
   const winners = Object.values(challengeVideos).filter(video => video.votes == highestVote);

   return winners; //[{winner}] || [{winner}, {winner}, {winner}, etc...]
}

//===== ACTIVE CHALLENGE WINNER. =====
export const returnChallengeWinner = returnVideoWinners => {
   const winners = returnVideoWinners
   return winners.length == 1 ? { [winners[0]._videoID]: { ...winners[0] } } : { TIE: {} };
}
//====================================


export const returnVideoLosers = (challengeVideos, returnVideoWinners) => {
   const _videoIDWinners = returnVideoWinners.map(video => video._videoID);
   return Object.values(challengeVideos).filter(video => !_videoIDWinners.includes(video._videoID)); //[{loser}, {loser}, {loser}] || []
}

//===== VIDEO RECORD UPDATE FUNCTIONS. =====
export const updateWinnerAndTiedVideoRecords = (returnVideoWinners, timedChallengesRef) => {
   const returnUpdatedVideoRecords = returnVideoWinners.map(video => {
      const { _videoID } = video;
      let { firebaseAllVideos } = timedChallengesRef
      let { record, record: { wins, losses, ties, winPct } } = firebaseAllVideos[_videoID]; //get current record for firebaseAllVideos pulled directly from firebase.

      if (returnVideoWinners.length == 1) wins += 1; //WINNER!!!
      else ties += 1; //TIED!!!
      winPct = (wins) / (wins + losses + ties);
      record = { ...record, wins, ties, winPct };
      firebaseAllVideos[_videoID].record = { ...record }; //Update ref.current.firebaseAllVideos
      timedChallengesRef.changedVideos = [...timedChallengesRef.changedVideos, { ...firebaseAllVideos[_videoID] }];
      return { ...video, record };
   });

   return returnUpdatedVideoRecords;
}

export const updateVideoLosingRecords = (returnVideoLosers, timedChallengesRef) => {
   if (!returnVideoLosers.length) return []
   else {
      return returnVideoLosers.map(video => {
         const { _videoID } = video;
         let { firebaseAllVideos, changedVideos } = timedChallengesRef
         let { record, record: { wins, losses, ties, winPct } } = firebaseAllVideos[_videoID];

         losses += 1;
         winPct = (wins) / (wins + losses + ties);
         record = { ...record, losses, winPct };

         firebaseAllVideos[_videoID].record = { ...record }; //Updating the ref.current.firebaseAllVideos;
         changedVideos = [...changedVideos, { ...firebaseAllVideos[_videoID] }];
         timedChallengesRef.changedVideos = [...timedChallengesRef.changedVideos, { ...firebaseAllVideos[_videoID] }];

         return { ...video, record };
      })
   }
}
//============================================

export const reduceAllVideosForChallenges = (updateWinnerAndTiedVideoRecords, updateVideoLosingRecords) => [...updateWinnerAndTiedVideoRecords, ...updateVideoLosingRecords].reduce((accumulator, video_object) => {
   accumulator = { ...accumulator, [video_object._videoID]: { ...video_object } };

   return accumulator;
}, {});

//===== UPDATED RECORDS & CHALLENGE WINNER FOR ***EACH*** CHALLENGE. =====
export const updateActiveChallenge = (challenge, returnChallengeWinner, reduceAllVideosForChallengs) => ({
   ...challenge,
   activeChallengeProperties: {
      ...challenge.activeChallengeProperties,
      winner: { ...returnChallengeWinner }
   },
   challengeVideos: {
      ...challenge.challengeVideos,
      ...reduceAllVideosForChallengs
   }
});
//========================================================================